import { IconFont } from '@byecode/ui'
import { NavigationShowMode } from '@lighthouse/core'
import { findParentScroller } from '@lighthouse/tools'
import { MantineProvider, Popover } from '@mantine/core'
import { useCurrentEditor, useEditorState } from '@tiptap/react'
import type { FC } from 'react'
import React, { startTransition, useEffect, useMemo, useRef, useState } from 'react'
import styled from 'styled-components'
import { debounce } from 'throttle-debounce'

import { useAppContainerContext, type VariableSelectDropDownProps } from '../../'
import type { TiptapConfig } from '../types'
import { CodeMenu, ImageMenu, LatexMenu, LineMenu, LinkMenu, QuoteMenu, VideoMenu } from './AdvancedGroup'
import { AlignMenu } from './Align'
import { BgColorMenu, TextColorMenu } from './BasicGroup'
import { EraserMenu } from './Eraser'
import { FontSizeMenu } from './FontSize'
import { HeadingMenu } from './Heading'
import { LineHeightMenu } from './LineHeight'
import { PostSegmentDistanceMenu } from './SegmentDistance/PostSegmentDistance'
import { PreSegmentDistanceMenu } from './SegmentDistance/PreSegmentDistance'
import { CustomActionIcon } from './styles'
import { TableMenu } from './TableMenu'
import { TextGroup } from './Text'
import { VariableMenu } from './Variable'

const Container = styled.div`
    position: relative;
    box-sizing: border-box;
    height: 40px;
    display: flex;
    justify-content: space-between;
    align-items: center;
    flex-wrap: nowrap;
    padding: 8px;
    background-color: var(--color-white);
    border-top-left-radius: 8px;
    border-top-right-radius: 8px;
`

const SCxContainerDisabledMask = styled.div`
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    border-radius: 8px;
    background-color: rgba(255, 255, 255, 0.5);
    cursor: not-allowed;
`

const MenuList = styled.div`
    height: 24px;
    flex: 1;
    display: flex;
    align-items: center;
    gap: 8px;
    overflow: hidden;

    iconpark-icon {
        color: var(--color-gray-400);
    }
`

const DropdownContainer = styled(Container)`
    gap: 8px;
    border-radius: 6px;
    border-bottom: none;
`
export type ToolbarStyles = 'toolbarContainer' | 'toolbarMenuList'
export interface ToolbarProps {
    config?: TiptapConfig
    disabled?: boolean
    isMobile?: boolean
    styles?: Partial<Record<ToolbarStyles, React.CSSProperties>>
}

type ToolbarListType = {
    key: string
    group: 'textStyle' | 'segment' | 'advanced' | 'variable' | 'image' | 'video' | 'table'
    component: FC<Partial<Pick<VariableSelectDropDownProps, 'pageOptions' | 'upstreamOptions' | 'userOption' | 'dateOption'>>>
}

const toolbarBtnList: ToolbarListType[] = [
    // { key: 'undo', group: 'advanced', component: UndoMenu },
    // { key: 'redo', group: 'advanced', component: RedoMenu },

    { key: 'heading', group: 'advanced', component: HeadingMenu },

    { key: 'variable', group: 'variable', component: VariableMenu },
    { key: 'eraser', group: 'textStyle', component: EraserMenu },
    { key: 'fontSize', group: 'textStyle', component: FontSizeMenu },
    { key: 'textGroup', group: 'textStyle', component: TextGroup },

    // { key: 'bold', group: 'basic', component: BoldMenu },
    // { key: 'italic', group: 'basic', component: ItalicMenu },
    // { key: 'strike', group: 'basic', component: StrikeMenu },
    // { key: 'underline', group: 'basic', component: UnderlineMenu },

    { key: 'textColor', group: 'textStyle', component: TextColorMenu },
    { key: 'bgColor', group: 'textStyle', component: BgColorMenu },
    { key: 'align', group: 'textStyle', component: AlignMenu },
    { key: 'lineHeight', group: 'textStyle', component: LineHeightMenu },
    { key: 'preSegmentDistance', group: 'segment', component: PreSegmentDistanceMenu },
    { key: 'postSegmentDistance', group: 'segment', component: PostSegmentDistanceMenu },

    // { key: 'orderedList', group: 'advanced', component: OrderedListMenu },
    // { key: 'bulletList', group: 'advanced', component: BulletListMenu },
    // { key: 'taskList', group: 'advanced', component: TaskListMenu },

    { key: 'link', group: 'textStyle', component: LinkMenu },
    { key: 'latex', group: 'textStyle', component: LatexMenu },
    { key: 'codeBlock', group: 'advanced', component: CodeMenu },
    { key: 'table', group: 'table', component: TableMenu },
    { key: 'image', group: 'image', component: ImageMenu },
    { key: 'video', group: 'video', component: VideoMenu },
    { key: 'quote', group: 'advanced', component: QuoteMenu },
    { key: 'line', group: 'advanced', component: LineMenu }
]

export const Toolbar: FC<ToolbarProps> = ({ config, disabled, isMobile, styles }) => {
    const { variable, image, video, mode } = config ?? {}

    const ref = useRef<HTMLDivElement | null>(null)

    // 超出隐藏的下标
    const [collapseIndex, setCollapseIndex] = useState(-1)

    const menuList = useMemo(() => {
        let list = toolbarBtnList

        if (!variable) {
            list = list.filter(item => item.key !== 'variable')
        }
        if (!image) {
            list = list.filter(item => item.key !== 'image')
        }
        if (!video) {
            list = list.filter(item => item.key !== 'video')
        }

        switch (mode) {
            case 'text-style': {
                list = list.filter(
                    item => item.group === 'textStyle' || item.group === 'variable' || item.group === 'image' || item.group === 'video'
                )
                break
            }
            case 'all': {
                break
            }

            default: {
                list = list.filter(item => item.group !== 'textStyle' && item.group !== 'advanced')
                break
            }
        }
        return list
    }, [image, video, mode, variable])

    /** 监听尺寸变化，计算需要收起的工具栏按钮 */
    useEffect(() => {
        const content = ref.current
        if (!content || isMobile) {
            return
        }

        const childWidthCache: number[] = []

        const handleResize = debounce(1000 / 60, (entries: ResizeObserverEntry[]) => {
            const [entry] = entries
            const contentWidth = entry.contentRect.width
            const { length } = content.children
            const gap = 8
            let reverseIndex = -1
            let currentChildWidth = 0

            for (let index = 0; index < length; index++) {
                const element = content.children.item(index)
                if (!element) {
                    break
                }

                if (element.clientWidth !== 0) {
                    childWidthCache.splice(index, 1, element.getBoundingClientRect().width)
                }
            }

            for (const [index, width] of childWidthCache.entries()) {
                currentChildWidth += width + gap

                if (currentChildWidth > contentWidth) {
                    reverseIndex = index
                    break
                }
            }

            setCollapseIndex(reverseIndex)
        })
        const observer = new ResizeObserver(handleResize)

        observer.observe(content)

        return () => observer.unobserve(content)
    }, [menuList.length, isMobile])

    const { editor } = useCurrentEditor()
    const editorState = useEditorState({ editor, selector: s => ({ isFocused: s.editor?.isFocused, isEditable: s.editor?.isEditable }) })
    const { isFocused, isEditable } = editorState ?? {}

    const [toolbarFocused, setToolbarFocused] = useState(false)
    const { showMode, isSticky, height } = useAppContainerContext()
    const navbarOffset = showMode === NavigationShowMode.horizontal && isSticky && !isMobile ? height * 16 : 0

    /** 监听交叉区域，吸附时添加阴影 */
    const rootRef = useRef<HTMLDivElement | null>(null)
    useEffect(() => {
        const root = rootRef.current
        if (!root) {
            return
        }

        const observer = new IntersectionObserver(
            ([e]) => {
                if (e.intersectionRatio < 1) {
                    root.style.setProperty('border-bottom', '1px solid var(--color-gray-200)')
                } else {
                    root.style.removeProperty('border-bottom')
                }
            },
            { threshold: [1], root: findParentScroller(root), rootMargin: `${-navbarOffset - 1}px 0px 0px 0px` }
        )

        if (isFocused || toolbarFocused) {
            observer.observe(root)
        }

        return () => {
            root.style.removeProperty('border-bottom')
            observer.unobserve(root)
        }
    }, [isFocused, toolbarFocused, navbarOffset])

    return (
        <MantineProvider
            inherit
            theme={{
                colors: {
                    blue: [
                        'var(--color-theme-1)',
                        'var(--color-theme-2)',
                        'var(--color-theme-3)',
                        'var(--color-theme-4)',
                        'var(--color-theme-5)',
                        'var(--color-theme-6)',
                        'var(--color-theme-7)',
                        'var(--color-theme-8)',
                        'var(--color-theme-9)',
                        'var(--color-theme-10)'
                    ]
                }
            }}
        >
            <Container
                ref={rootRef}
                tabIndex={-1}
                onFocusCapture={e => {
                    setToolbarFocused(true)
                }}
                onBlurCapture={() => startTransition(() => setToolbarFocused(false))}
                style={{
                    ...styles?.toolbarContainer,
                    ...(isEditable && (isFocused || toolbarFocused)
                        ? { position: 'sticky', top: 0, zIndex: 1, willChange: 'transform' }
                        : {})
                }}
            >
                <MenuList ref={ref} style={styles?.toolbarMenuList}>
                    {menuList.slice(0, collapseIndex === -1 ? menuList.length : collapseIndex).map(item => (
                        <item.component key={item.key} {...(item.group === 'variable' ? variable : {})} />
                    ))}
                </MenuList>
                {/* 超出时显示的菜单按钮 */}
                <Popover
                    withinPortal
                    position="bottom"
                    positionDependencies={[collapseIndex]}
                    shadow="md"
                    styles={{ dropdown: { padding: 0 } }}
                >
                    <Popover.Target>
                        <CustomActionIcon
                            style={{
                                display: collapseIndex === -1 ? 'none' : 'block',
                                pointerEvents: collapseIndex === -1 ? 'none' : undefined
                            }}
                        >
                            <IconFont type="DotsThree" />
                        </CustomActionIcon>
                    </Popover.Target>
                    <Popover.Dropdown>
                        <DropdownContainer>
                            {menuList.slice(collapseIndex, collapseIndex === -1 ? collapseIndex : undefined).map(item => (
                                <item.component key={`collapse-${item.key}`} {...variable} />
                            ))}
                        </DropdownContainer>
                    </Popover.Dropdown>
                </Popover>
                {disabled && <SCxContainerDisabledMask />}
            </Container>
        </MantineProvider>
    )
}
